/** Translations may lag English (the fallback), so other languages are partial. */
type DeepPartial<T> = { [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K] };

/** Strings shared by all three apps (namespace "common"). */
export const commonEn = {
  actions: {
    save: 'Save',
    saveChanges: 'Save changes',
    cancel: 'Cancel',
    close: 'Close',
    edit: 'Edit',
    delete: 'Delete',
    add: 'Add',
    create: 'Create',
    confirm: 'Confirm',
    back: 'Back',
    next: 'Next',
    search: 'Search',
    filter: 'Filter',
    clear: 'Clear',
    retry: 'Try again',
    download: 'Download',
    print: 'Print',
    viewAll: 'View all',
    signOut: 'Sign out',
    signIn: 'Sign in',
    loading: 'Loading…',
    more: 'More',
    done: 'Done',
    share: 'Share',
    copy: 'Copy',
  },
  status: {
    scheduled: 'Scheduled',
    confirmed: 'Confirmed',
    checked_in: 'Checked in',
    in_progress: 'In progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
    no_show: 'No-show',
    draft: 'Draft',
    posted: 'Posted',
    void: 'Void',
    credited: 'Credited',
    paid: 'Paid',
    partially_paid: 'Partly paid',
    unpaid: 'Unpaid',
    active: 'Active',
    inactive: 'Inactive',
    discharged: 'Discharged',
    signed: 'Signed',
    suspended: 'Suspended',
    exhausted: 'Used up',
    expired: 'Expired',
    new: 'New',
    contacted: 'Contacted',
    follow_up: 'Follow-up',
    converted: 'Converted',
    lost: 'Lost',
    submitted: 'Claim submitted',
    approved: 'Claim approved',
    rejected: 'Claim rejected',
    settled: 'Claim settled',
    queued: 'Queued',
    sent: 'Sent',
    delivered: 'Delivered',
    failed: 'Failed',
    manual: 'Send manually',
    pending: 'Pending',
    finalized: 'Finalized',
    present: 'Present',
    half_day: 'Half day',
    absent: 'Absent',
    leave: 'On leave',
    holiday: 'Holiday',
    open: 'Open',
    waiting_on_clinic: 'Awaiting your reply',
    resolved: 'Resolved',
    closed: 'Closed',
  },
  appointmentType: {
    clinic: 'In clinic',
    home_visit: 'Home visit',
    telehealth: 'Telehealth',
  },
  gender: {
    male: 'Male',
    female: 'Female',
    other: 'Other',
    undisclosed: 'Prefer not to say',
  },
  paymentMethod: {
    cash: 'Cash',
    card: 'Card',
    upi: 'UPI',
    bank_transfer: 'Bank transfer',
    insurance: 'Insurance',
    online: 'Online',
  },
  errors: {
    generic: 'Something went wrong. Please try again.',
    network: 'Cannot reach the server. Check your connection.',
    forbidden: 'You do not have permission to do that.',
    notFound: 'We could not find that.',
    sessionExpired: 'Your session has expired. Please sign in again.',
    required: 'This field is required',
  },
  theme: { light: 'Light', dark: 'Dark', system: 'System', toggle: 'Toggle theme' },
  language: 'Language',
  empty: 'Nothing here yet',
  pagination: { previous: 'Previous', next: 'Next', page: 'Page {{page}} of {{total}}' },
  units: { minutes: '{{count}} min', sets: '{{count}} sets', reps: '{{count}} reps', hold: 'hold {{count}}s' },
};

export const commonAr: DeepPartial<typeof commonEn> = {
  actions: {
    save: 'حفظ',
    saveChanges: 'حفظ التغييرات',
    cancel: 'إلغاء',
    close: 'إغلاق',
    edit: 'تعديل',
    delete: 'حذف',
    add: 'إضافة',
    create: 'إنشاء',
    confirm: 'تأكيد',
    back: 'رجوع',
    next: 'التالي',
    search: 'بحث',
    filter: 'تصفية',
    clear: 'مسح',
    retry: 'إعادة المحاولة',
    download: 'تنزيل',
    print: 'طباعة',
    viewAll: 'عرض الكل',
    signOut: 'تسجيل الخروج',
    signIn: 'تسجيل الدخول',
    loading: 'جارٍ التحميل…',
    more: 'المزيد',
  },
  status: {
    scheduled: 'مجدول',
    confirmed: 'مؤكد',
    checked_in: 'تم الحضور',
    in_progress: 'قيد الجلسة',
    completed: 'مكتمل',
    cancelled: 'ملغى',
    no_show: 'لم يحضر',
    draft: 'مسودة',
    posted: 'صادرة',
    void: 'ملغاة',
    credited: 'مُعادة',
    paid: 'مدفوعة',
    partially_paid: 'مدفوعة جزئيًا',
    unpaid: 'غير مدفوعة',
    active: 'نشط',
    inactive: 'غير نشط',
    discharged: 'مُنهى العلاج',
    signed: 'موقّع',
    suspended: 'موقوف',
    exhausted: 'مستنفد',
    expired: 'منتهي',
    new: 'جديد',
    contacted: 'تم التواصل',
    follow_up: 'متابعة',
    converted: 'تم التحويل',
    lost: 'مفقود',
    submitted: 'مطالبة مقدمة',
    approved: 'مطالبة مقبولة',
    rejected: 'مطالبة مرفوضة',
    settled: 'مطالبة مسددة',
    queued: 'في الانتظار',
    sent: 'أُرسلت',
    delivered: 'تم التسليم',
    failed: 'فشل',
    manual: 'إرسال يدوي',
    pending: 'قيد الانتظار',
    finalized: 'معتمد',
    present: 'حاضر',
    half_day: 'نصف يوم',
    absent: 'غائب',
    leave: 'في إجازة',
    holiday: 'عطلة',
    open: 'مفتوحة',
    waiting_on_clinic: 'بانتظار ردك',
    resolved: 'تم الحل',
    closed: 'مغلقة',
  },
  appointmentType: { clinic: 'في العيادة', home_visit: 'زيارة منزلية', telehealth: 'عن بُعد' },
  gender: { male: 'ذكر', female: 'أنثى', other: 'آخر', undisclosed: 'أفضل عدم الإفصاح' },
  paymentMethod: { cash: 'نقدًا', card: 'بطاقة', upi: 'UPI', bank_transfer: 'تحويل بنكي', insurance: 'تأمين', online: 'إلكتروني' },
  errors: {
    generic: 'حدث خطأ ما. يرجى المحاولة مرة أخرى.',
    network: 'تعذر الاتصال بالخادم. تحقق من اتصالك.',
    forbidden: 'ليس لديك صلاحية للقيام بذلك.',
    notFound: 'لم نتمكن من العثور على ذلك.',
    sessionExpired: 'انتهت جلستك. يرجى تسجيل الدخول مرة أخرى.',
    required: 'هذا الحقل مطلوب',
  },
  theme: { light: 'فاتح', dark: 'داكن', system: 'النظام', toggle: 'تبديل المظهر' },
  language: 'اللغة',
  empty: 'لا يوجد شيء هنا بعد',
  pagination: { previous: 'السابق', next: 'التالي', page: 'صفحة {{page}} من {{total}}' },
  units: { minutes: '{{count}} دقيقة', sets: '{{count}} مجموعات', reps: '{{count}} تكرار', hold: 'ثبات {{count}} ث' },
};

export const commonMl: DeepPartial<typeof commonEn> = {
  actions: {
    save: 'സേവ് ചെയ്യുക',
    saveChanges: 'മാറ്റങ്ങൾ സേവ് ചെയ്യുക',
    cancel: 'റദ്ദാക്കുക',
    close: 'അടയ്ക്കുക',
    edit: 'തിരുത്തുക',
    delete: 'ഇല്ലാതാക്കുക',
    add: 'ചേർക്കുക',
    create: 'സൃഷ്ടിക്കുക',
    confirm: 'സ്ഥിരീകരിക്കുക',
    back: 'തിരികെ',
    next: 'അടുത്തത്',
    search: 'തിരയുക',
    filter: 'ഫിൽട്ടർ',
    clear: 'മായ്ക്കുക',
    retry: 'വീണ്ടും ശ്രമിക്കുക',
    download: 'ഡൗൺലോഡ്',
    print: 'പ്രിന്റ്',
    viewAll: 'എല്ലാം കാണുക',
    signOut: 'സൈൻ ഔട്ട്',
    signIn: 'സൈൻ ഇൻ',
    loading: 'ലോഡ് ചെയ്യുന്നു…',
    more: 'കൂടുതൽ',
  },
  status: {
    scheduled: 'ഷെഡ്യൂൾ ചെയ്തു',
    confirmed: 'സ്ഥിരീകരിച്ചു',
    checked_in: 'എത്തി',
    in_progress: 'നടക്കുന്നു',
    completed: 'പൂർത്തിയായി',
    cancelled: 'റദ്ദാക്കി',
    no_show: 'വന്നില്ല',
    draft: 'ഡ്രാഫ്റ്റ്',
    posted: 'പോസ്റ്റ് ചെയ്തു',
    void: 'അസാധുവാക്കി',
    credited: 'ക്രെഡിറ്റ് ചെയ്തു',
    paid: 'അടച്ചു',
    partially_paid: 'ഭാഗികമായി അടച്ചു',
    unpaid: 'അടച്ചിട്ടില്ല',
    active: 'സജീവം',
    inactive: 'നിഷ്ക്രിയം',
    discharged: 'ഡിസ്ചാർജ് ചെയ്തു',
    signed: 'ഒപ്പിട്ടു',
    suspended: 'താൽക്കാലികമായി നിർത്തി',
    exhausted: 'തീർന്നു',
    expired: 'കാലാവധി കഴിഞ്ഞു',
    new: 'പുതിയത്',
    contacted: 'ബന്ധപ്പെട്ടു',
    follow_up: 'ഫോളോ-അപ്പ്',
    converted: 'രോഗിയായി ചേർത്തു',
    lost: 'നഷ്ടപ്പെട്ടു',
    submitted: 'ക്ലെയിം സമർപ്പിച്ചു',
    approved: 'ക്ലെയിം അംഗീകരിച്ചു',
    rejected: 'ക്ലെയിം നിരസിച്ചു',
    settled: 'ക്ലെയിം തീർപ്പാക്കി',
    queued: 'ക്യൂവിൽ',
    sent: 'അയച്ചു',
    delivered: 'ലഭിച്ചു',
    failed: 'പരാജയപ്പെട്ടു',
    manual: 'നേരിട്ട് അയയ്ക്കണം',
    pending: 'പരിഗണനയിൽ',
    finalized: 'അന്തിമമാക്കി',
    present: 'ഹാജർ',
    half_day: 'അർദ്ധ ദിവസം',
    absent: 'ഹാജരില്ല',
    leave: 'അവധിയിൽ',
    holiday: 'അവധി ദിനം',
    open: 'തുറന്നത്',
    waiting_on_clinic: 'നിങ്ങളുടെ മറുപടി കാത്തിരിക്കുന്നു',
    resolved: 'പരിഹരിച്ചു',
    closed: 'അടച്ചു',
  },
  appointmentType: { clinic: 'ക്ലിനിക്കിൽ', home_visit: 'ഹോം വിസിറ്റ്', telehealth: 'ടെലിഹെൽത്ത്' },
  gender: { male: 'പുരുഷൻ', female: 'സ്ത്രീ', other: 'മറ്റുള്ളവ', undisclosed: 'പറയാൻ താൽപ്പര്യമില്ല' },
  paymentMethod: { cash: 'പണം', card: 'കാർഡ്', upi: 'UPI', bank_transfer: 'ബാങ്ക് ട്രാൻസ്ഫർ', insurance: 'ഇൻഷുറൻസ്', online: 'ഓൺലൈൻ' },
  errors: {
    generic: 'എന്തോ തകരാർ സംഭവിച്ചു. വീണ്ടും ശ്രമിക്കുക.',
    network: 'സെർവറുമായി ബന്ധപ്പെടാൻ കഴിയുന്നില്ല. ഇന്റർനെറ്റ് കണക്ഷൻ പരിശോധിക്കുക.',
    forbidden: 'ഇത് ചെയ്യാൻ നിങ്ങൾക്ക് അനുമതിയില്ല.',
    notFound: 'ഇത് കണ്ടെത്താനായില്ല.',
    sessionExpired: 'നിങ്ങളുടെ സെഷൻ കാലഹരണപ്പെട്ടു. വീണ്ടും സൈൻ ഇൻ ചെയ്യുക.',
    required: 'ഈ ഫീൽഡ് നിർബന്ധമാണ്',
  },
  theme: { light: 'ലൈറ്റ്', dark: 'ഡാർക്ക്', system: 'സിസ്റ്റം', toggle: 'തീം മാറ്റുക' },
  language: 'ഭാഷ',
  empty: 'ഇവിടെ ഒന്നുമില്ല',
  pagination: { previous: 'മുമ്പത്തേത്', next: 'അടുത്തത്', page: 'പേജ് {{page}} / {{total}}' },
  units: { minutes: '{{count}} മിനിറ്റ്', sets: '{{count}} സെറ്റ്', reps: '{{count}} ആവർത്തനം', hold: '{{count}} സെ. പിടിക്കുക' },
};
